import { CYCLE_STEPS, stepCompletion, type CycleStep } from "@domain/steps.js";
import type { Decidable } from "@domain/decision-state.js";

/**
 * The seven-step rail.
 *
 * **It imports the real predicate rather than reimplementing it.** Which
 * states block which step is not uniform — a rejection blocks step 4 and
 * deliberately does not block step 7 — and a second copy of that table in the
 * front end would eventually disagree with the back end. The first symptom
 * would be a gate that lets a user continue while the API refuses, which is
 * unpleasant to diagnose from either side.
 */

const STEP_LABELS: Readonly<Record<CycleStep, string>> = {
  describe: "Describe",
  clarify: "Clarify",
  backlog: "Backlog",
  prototype: "Prototype",
  plan: "Plan",
  build: "Build",
  review: "Review",
};

export interface StepRailProps {
  /** What each step governs. Absent means the step governs nothing yet. */
  readonly governed: Readonly<Partial<Record<CycleStep, readonly Decidable[]>>>;
  readonly current: CycleStep;
  readonly onSelect: (step: CycleStep) => void;
}

export const StepRail = ({
  governed,
  current,
  onSelect,
}: StepRailProps): React.JSX.Element => (
  <nav aria-label="Build cycle steps" className="rail">
    <ol>
      {CYCLE_STEPS.map((step, index) => {
        const completion = stepCompletion(step, governed[step] ?? []);
        const isCurrent = step === current;

        return (
          <li key={step}>
            <button
              type="button"
              onClick={() => onSelect(step)}
              aria-current={isCurrent ? "step" : undefined}
              className={isCurrent ? "step step-current" : "step"}
            >
              <span className="step-index">{index + 1}</span>
              <span className="step-label">{STEP_LABELS[step]}</span>
              {/*
                Principle 7: progress is a number. Never a bare tick — a user
                should not have to guess whether they are nearly done. The
                state is also spelled out in text rather than carried by colour
                alone (BLG-001-AC2).
              */}
              <span className="step-progress">
                {completion.total === 0
                  ? "nothing yet"
                  : completion.complete
                    ? `all ${completion.total} settled`
                    : `${completion.settled} of ${completion.total} settled`}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  </nav>
);
